import { useCallback, useMemo, useRef, useState } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import { XIcon } from 'lucide-react';
import { Resolver, useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { InfoTooltip } from '@/components/atoms/InfoTooltip/InfoTooltip';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { PhoneInput } from '@/components/ui/phone-input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { capitalizeFirstLetter } from '@/util/util';

import { useCampaigns } from '../hooks/useCampaigns';
import { DraftCampaignSubscriber } from '../redux/actions';
import { handleValidationErrors } from '../utils';
import { quickAddSchema } from '../validator';

interface QuickAddForm {
  name?: string;
  email?: string;
  phone?: string;
  keepAdding: boolean;
}

export interface QuickAddPopoverProps {
  children: React.ReactNode;
  onAdd: (subscriber: DraftCampaignSubscriber) => void;
  existingSubscribers?: DraftCampaignSubscriber[];
  align?: 'start' | 'center' | 'end';
}

export const QuickAddPopover = ({
  children,
  onAdd,
  existingSubscribers = [],
  align = 'start',
}: QuickAddPopoverProps) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const contentRef = useRef<HTMLDivElement | null>(null);
  const { verifySubscribersAsync } = useCampaigns();

  const schema = useMemo(() => quickAddSchema(existingSubscribers), [existingSubscribers]);
  const form = useForm<QuickAddForm>({
    resolver: yupResolver(schema) as Resolver<QuickAddForm>,
    defaultValues: { name: '', email: '', phone: '', keepAdding: false },
  });

  const keepAdding = useWatch({ control: form.control, name: 'keepAdding' });
  const submit = useCallback(
    async (values: QuickAddForm, keepOpenAfterAdd: boolean) => {
      const trimmedEmail = values.email?.trim();
      const trimmedPhone = values.phone?.trim();

      const subscriber: DraftCampaignSubscriber = {};
      if (values.name && values.name.trim()) {
        subscriber['user.display_name'] = values.name.trim();
      }
      if (trimmedEmail) {
        subscriber.email = trimmedEmail;
      }
      if (trimmedPhone) {
        subscriber.phone = trimmedPhone;
      }

      // Create verification payload with all subscribers (existing + new)
      const allSubscribers = [...existingSubscribers, subscriber];
      const verificationPayload = {
        subscribers: allSubscribers.map((sub) => ({
          email: sub.email,
          phone: sub.phone,
          context: {},
          metadata: {},
        })),
      };

      try {
        setIsVerifying(true);

        const verificationResult = await verifySubscribersAsync(verificationPayload);

        // Handle validation errors using utility function
        const hasValidationErrors = handleValidationErrors(
          verificationResult,
          existingSubscribers,
          form,
          t
        );

        setIsVerifying(false);

        if (hasValidationErrors) {
          return;
        }

        onAdd(subscriber);

        // Show success toast
        toast.success(t('campaigns.contacts_added', { count: 1 }));

        const shouldKeepOpen = keepOpenAfterAdd || values.keepAdding;
        if (shouldKeepOpen) {
          form.reset({ name: '', email: '', phone: '', keepAdding: values.keepAdding });
        } else {
          setOpen(false);
          setTimeout(() => form.reset({ name: '', email: '', phone: '', keepAdding: false }), 0);
        }
      } catch (error) {
        // Handle network or other genuine errors (not validation responses)
        console.error('Verification failed:', error);
        toast.error(t('campaigns.verification_failed'));
        setIsVerifying(false);
      }
    },
    [form, onAdd, t, existingSubscribers, verifySubscribersAsync]
  );

  return (
    <Popover
      open={open}
      onOpenChange={(newOpen) => {
        // When keepAdding is checked, block automatic closing (e.g. from parent re-renders).
        // User can still close via the X button which calls setOpen(false) directly.
        if (!newOpen && keepAdding) {
          return;
        }
        setOpen(newOpen);
      }}
    >
      <PopoverTrigger asChild>{children}</PopoverTrigger>
      <PopoverContent
        ref={contentRef}
        align={align}
        side="bottom"
        className="flex max-h-[50vh] min-h-[380px] w-sm flex-col p-0"
        sideOffset={0}
        avoidCollisions={false}
        onEscapeKeyDown={() => {
          form.reset({ name: '', email: '', phone: '', keepAdding: false });
          setOpen(false);
        }}
        onInteractOutside={(event) => {
          const { name, email, phone } = form.getValues();
          const hasTyped = !!name?.trim() || !!email?.trim() || !!phone?.trim();

          if (hasTyped) {
            event.preventDefault();
            void form.trigger();
            return;
          }

          form.reset({ name: '', email: '', phone: '', keepAdding: false });
        }}
      >
        {/* Fixed Header */}
        <div className="relative flex flex-col gap-1 border-b border-border px-4 pt-4 pb-3">
          <div className="flex flex-col gap-1">
            <div className="text-xl leading-none font-medium">{t('campaigns.setup.quick_add')}</div>
            <div className="text-sm leading-5 text-foreground-muted">
              {t('campaigns.setup.quick_add_helper')}
            </div>
          </div>
          <InfoTooltip content={t('common.close')}>
            <Button
              type="button"
              variant="ghost-neutral"
              size="icon-sm"
              aria-label={t('common.close')}
              className="absolute top-3 right-3"
              onClick={() => {
                form.reset({ name: '', email: '', phone: '', keepAdding: false });
                setOpen(false);
              }}
            >
              <XIcon size={16} className="text-foreground" />
            </Button>
          </InfoTooltip>
        </div>

        {/* Scrollable Form Content */}
        <Form {...form}>
          <div className="flex-1 overflow-y-auto px-4 pt-4 pb-5">
            <form
              onSubmit={(e) => {
                e.preventDefault();
                form.handleSubmit((values) => submit(values, false))();
              }}
              className="space-y-3"
            >
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('common.name')}</FormLabel>
                    <FormControl>
                      <Input {...field} autoComplete="off" autoFocus />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{capitalizeFirstLetter(t('common.email'))}</FormLabel>
                    <FormControl>
                      <Input
                        type="email"
                        {...field}
                        autoComplete="off"
                        onChange={(e) => {
                          field.onChange(e);
                          // Clear email error when user starts typing
                          if (form.formState.errors.email) {
                            form.clearErrors('email');
                          }
                        }}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="phone"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('common.phone')}</FormLabel>
                    <FormControl>
                      <PhoneInput
                        value={field.value ?? ''}
                        onChange={(val) => {
                          field.onChange(val);
                          if (form.formState.errors.phone) {
                            form.clearErrors('phone');
                          }
                        }}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </form>
          </div>

          {/* Fixed Footer */}
          <div className="border-t border-border p-4 pt-3">
            <div className="flex items-center justify-between gap-2">
              <FormField
                control={form.control}
                name="keepAdding"
                render={({ field }) => (
                  <FormItem className="flex items-center gap-2 space-y-0 rounded-md">
                    <FormControl>
                      <Checkbox checked={field.value} onCheckedChange={field.onChange} />
                    </FormControl>
                    <FormLabel className="mb-0 text-sm leading-none font-medium">
                      {t('campaigns.setup.keep_adding')}
                    </FormLabel>
                  </FormItem>
                )}
              />
              <Button
                type="button"
                size="sm"
                isLoading={isVerifying}
                onClick={() => form.handleSubmit((values) => submit(values, keepAdding))()}
              >
                {keepAdding ? t('campaigns.setup.add_and_continue') : t('campaigns.setup.add')}
              </Button>
            </div>
          </div>
        </Form>
      </PopoverContent>
    </Popover>
  );
};
